import React from 'react'

import { Box, Chip, NoSsr, Paper, Stack, Typography } from '@mui/material'
import { BarPlot } from '@mui/x-charts/BarChart'
import { ResponsiveChartContainer } from '@mui/x-charts/ResponsiveChartContainer'
import { ChartsGrid } from '@mui/x-charts/ChartsGrid'
import { ChartsTooltip } from '@mui/x-charts/ChartsTooltip'
import { ChartsXAxis } from '@mui/x-charts/ChartsXAxis'
import { ChartsYAxis } from '@mui/x-charts/ChartsYAxis'
import { LinePlot, MarkPlot } from '@mui/x-charts/LineChart'
import type { ChartsAxisContentProps } from '@mui/x-charts/ChartsTooltip'

import * as COLORS from '@/app/theme/colors/flyin'

interface ChartDatasetRecord {
    [key: string]: string | number | Date | null | undefined
}

export interface ChartDataItem extends ChartDatasetRecord {
    high: number
    low: number
    rainfall: number
    month: string
}

interface BestTimeToVisitProps {
    title: string
    summary: string
    bestMonths: string[]
    chartDataset: ChartDataItem[]
    tempMin: number
    tempMax: number
    TooltipSlot: React.ElementType<ChartsAxisContentProps>
}

interface WeatherTooltipContentProps {
    month: string
    high: number
    low: number
    rain: number
    condition: string
    note: string
}

const WEATHER_SERIES = [
    {
        type: 'line' as const,
        dataKey: 'high',
        color: COLORS.CHART_TEMP_HIGH,
        curve: 'natural' as const,
        showMark: true,
        area: true,
    },
    {
        type: 'line' as const,
        dataKey: 'low',
        color: COLORS.CHART_TEMP_LOW,
        curve: 'natural' as const,
        showMark: true,
    },
    {
        type: 'bar' as const,
        dataKey: 'rainfall',
        color: COLORS.CHART_RAINFALL,
        yAxisId: 'rainAxis',
    },
]

const LEGEND_ITEMS = [
    { color: COLORS.CHART_TEMP_HIGH, label: 'High °C' },
    { color: COLORS.CHART_TEMP_LOW, label: 'Low °C' },
    { color: COLORS.CHART_RAINFALL, label: 'Rainfall mm' },
]

const TOOLTIP_ROWS = (high: number, low: number, rain: number) => [
    { color: COLORS.CHART_TEMP_HIGH, label: 'High', value: `${high}°C` },
    { color: COLORS.CHART_TEMP_LOW, label: 'Low', value: `${low}°C` },
    { color: COLORS.CHART_RAINFALL, label: 'Rain', value: `${rain} mm` },
]

export const WeatherTooltipContent: React.FC<WeatherTooltipContentProps> = ({ month, high, low, rain, condition, note }) => (
    <Paper elevation={3} sx={{ p: 2, minWidth: 220, borderRadius: 2 }}>
        <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.5, fontSize: '0.9rem' }}>
            {month}
        </Typography>
        {condition && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1.25 }}>
                {condition}
            </Typography>
        )}
        {TOOLTIP_ROWS(high, low, rain).map(row => (
            <Box
                key={row.label}
                sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', py: 0.625 }}
            >
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: row.color, flexShrink: 0 }} />
                    <Typography variant="body2" color="text.secondary">{row.label}</Typography>
                </Box>
                <Typography variant="body2" sx={{ fontWeight: 700, ml: 4 }}>{row.value}</Typography>
            </Box>
        ))}
        {note && (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1.25, pt: 1.25, borderTop: '1px solid', borderColor: 'divider' }}>
                {note}
            </Typography>
        )}
    </Paper>
)

const BestTimeToVisit: React.FC<BestTimeToVisitProps> = ({ title, summary, bestMonths, chartDataset, tempMin, tempMax, TooltipSlot }) => {
    const tooltipSlots = React.useMemo(() => ({ axisContent: TooltipSlot }), [TooltipSlot])

    return (
        <Box component="section" sx={{ mb: 3 }}>
            {title && (
                <Typography
                    component="h2"
                    variant="h6"
                    sx={{ fontWeight: 600, fontSize: { xs: '1rem', md: '1.1rem' }, mb: 1.5 }}
                >
                    {title}
                </Typography>
            )}
            {summary && (
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.7 }}>
                    {summary}
                </Typography>
            )}
            {bestMonths.length > 0 && (
                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 2 }}>
                    {bestMonths.map(month => (
                        <Chip key={month} size="small" label={month} sx={{ bgcolor: 'grey.100', fontSize: '0.75rem' }} />
                    ))}
                </Box>
            )}
            {chartDataset.length > 0 && (
                <>
                    <Box
                        component="table"
                        aria-label="Monthly weather data"
                        sx={{
                            position: 'absolute',
                            width: '1px',
                            height: '1px',
                            padding: 0,
                            margin: '-1px',
                            overflow: 'hidden',
                            clip: 'rect(0 0 0 0)',
                            clipPath: 'inset(50%)',
                            whiteSpace: 'nowrap',
                            border: 0,
                        }}
                    >
                        <thead>
                            <tr>
                                <th>Month</th>
                                <th>High (&deg;C)</th>
                                <th>Low (&deg;C)</th>
                                <th>Rainfall (mm)</th>
                            </tr>
                        </thead>
                        <tbody>
                            {chartDataset.map(row => (
                                <tr key={row.month}>
                                    <td>{row.month}</td>
                                    <td>{row.high}</td>
                                    <td>{row.low}</td>
                                    <td>{row.rainfall}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Box>
                    <NoSsr>
                        <Paper variant="outlined" sx={{ borderRadius: 2, p: 2 }}>
                            <Stack direction="row" sx={{ mb: 1.5, flexWrap: 'wrap', gap: 1 }}>
                                {LEGEND_ITEMS.map(item => (
                                    <Stack key={item.label} direction="row" spacing={0.75} alignItems="center">
                                        <Box sx={{ width: 12, height: 12, borderRadius: '50%', bgcolor: item.color }} />
                                        <Typography variant="caption" color="text.secondary" sx={{ fontWeight: 600 }}>
                                            {item.label}
                                        </Typography>
                                    </Stack>
                                ))}
                            </Stack>
                            <ResponsiveChartContainer
                                dataset={chartDataset}
                                series={WEATHER_SERIES}
                                height={260}
                                margin={{ top: 16, right: 48, left: 40, bottom: 24 }}
                                xAxis={[
                                    {
                                        scaleType: 'band',
                                        dataKey: 'month',
                                        valueFormatter: (v: string) => v.substring(0, 3),
                                        tickLabelStyle: { fill: COLORS.CHART_AXIS_LABEL, fontSize: 11, fontWeight: 500 },
                                        disableLine: true,
                                        disableTicks: true,
                                    },
                                ]}
                                yAxis={[
                                    {
                                        id: 'tempAxis',
                                        min: tempMin,
                                        max: tempMax,
                                        tickNumber: 4,
                                        tickLabelStyle: { fill: COLORS.CHART_AXIS_LABEL, fontSize: 11 },
                                        disableLine: true,
                                        disableTicks: true,
                                    },
                                    {
                                        id: 'rainAxis',
                                        position: 'right',
                                        min: 0,
                                        max: Math.max(...chartDataset.map(d => d.rainfall as number), 10),
                                        tickNumber: 4,
                                        tickLabelStyle: { fill: COLORS.CHART_AXIS_LABEL, fontSize: 11 },
                                        disableLine: true,
                                        disableTicks: true,
                                    },
                                ]}
                            >
                                <ChartsGrid
                                    horizontal
                                    sx={{ '& line': { stroke: COLORS.CHART_GRID_LINE, strokeDasharray: '4 6' } }}
                                />
                                <BarPlot borderRadius={4} />
                                <LinePlot
                                    sx={{
                                        '& .MuiLineElement-root': { strokeWidth: 2.5 },
                                        '& .MuiAreaElement-root': { fill: COLORS.CHART_TEMP_HIGH, fillOpacity: 0.07 },
                                    }}
                                />
                                <MarkPlot sx={{ '& .MuiMarkElement-root': { strokeWidth: 0, r: 4 } }} />
                                <ChartsXAxis />
                                <ChartsYAxis axisId="tempAxis" />
                                <ChartsYAxis axisId="rainAxis" />
                                <ChartsTooltip trigger="axis" slots={tooltipSlots} />
                            </ResponsiveChartContainer>
                        </Paper>
                    </NoSsr>
                </>
            )}
        </Box>
    )
}

export default BestTimeToVisit
export type { BestTimeToVisitProps, WeatherTooltipContentProps }
